import { yupResolver } from '@hookform/resolvers/yup';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Tab } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  EstadosActivacionInstallOTEnumChoice,
  EstadosOrdenTrabajoEnumChoice,
  ROUTER_PATHS,
  useTabsFormOnly,
} from '@/shared';
import {
  OrdenTrabajoQueryKeysEnum,
} from '@/shared/actions/erp';
import {
  CustomButtunsRejectConfirm,
  CustomTabPanel,
  FormTabsOnly,
  SingleIconButton,
  TabsFormBoxScene,
} from '@/shared/components/ui';
import {
  AlertSeverityEnum,
  EquiposMaterialesUtilizados,
} from '@/shared/interfaces';
import { a11yProps } from '@/shared/utils';
import { useMaterialesUtilizadosStore } from '@/store/erp';
import { useUiConfirmModalStore, useUiStore } from '@/store/ui';
import { useEquiposDisponiblesFlotaOT } from '../../hooks/useEquiposDisponiblesFlotaOT';
import { EquiposUtilizadosOTTableType } from '../SaveOrdenTrabajo/equipos';
import { IOrdenServicioCorporativo } from '@/shared/interfaces/erp/orden_servicio_corporativo';
import OTInstalacionTecnicoCorporativoFormTab from '../SaveOrdenTrabajo/TabOrdenServicioCorporativoForm/OTInstalacionTecnicoCorporativoFormTab';
import OTDeralleInstallTecnicoCorporativoFormTab from '../SaveOrdenTrabajo/TabOrdenServicioCorporativoForm/OTDeralleInstallTecnicoCorporativoFormTab';
import OTDetalleClienteInstallTecnicoCorporativoFormTab from '../SaveOrdenTrabajo/TabOrdenServicioCorporativoForm/OTDetalleClienteInstallTecnicoCorporativoFormTab';
import OTMaterialesInstallTecnicoCorporativoFormTab from '../SaveOrdenTrabajo/TabOrdenServicioCorporativoForm/OTMaterialesInstallTecnicoCorporativoFormTab';
import { usePutOrdenServicioCorporativo } from '@/shared/actions/erp/tecnico/orden_servicio_corporativo';
import { ordenTrabajoCorporativoFormSchema } from '@/shared/utils/validation-schemas/erp/tecnico/orden-trabajo/orden-trabajo-corporativo.schema';
import RejectInstalacionAsigTecModal from '../tables/RejectInstalacionAsigTecModal';


export interface SaveOrdenTrabajoAsignadaTecCorporativoProps {
  title: string
  ordentrabajo?: IOrdenServicioCorporativo
  isCorrentingData?: boolean
}

const SaveOrdenTrabajoAsignadaTecCorporativo: React.FC<
  SaveOrdenTrabajoAsignadaTecCorporativoProps
> = ({ title, ordentrabajo, isCorrentingData = false }) => {
  const navigate = useNavigate();
  const returnUrlInstalacionesTecnicoPage =
    ROUTER_PATHS.tecnico.ordenenTrabajoNav;
  ///* hooks -----------------------
  const { handleMethodChange, valueTab } = useTabsFormOnly({
    initialTabValue: 1,
  });
  const queryClient = useQueryClient();

  // ================== EQUIPOS ==================
  useEquiposDisponiblesFlotaOT({ id: ordentrabajo?.id_orden_servicio_corporativo!, flota_data: ordentrabajo?.flota_data! }); ///*TODO: Activar para los equipos y materiales

  // console.log("debe estar la ubicacion", ordentrabajo?.flota_data)
  const clearAllMaterials = useMaterialesUtilizadosStore(
    s => s.clearAllMaterials
  );

  // ================== EQUIPOS --- END ==================

  ///* local state -----------------------
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [shrink] = useState(true);

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  const setAlert = useUiStore(s => s.setAlert);

  ///* form -----------------------
  //const form = useForm<SaveFormDataInstallTecAsig>({
  const form = useForm<any>({
    resolver: yupResolver(ordenTrabajoCorporativoFormSchema) as any,
    defaultValues: {
      estado: true,
      es_conexion_tierra: false,
      es_regulador: false,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* mutations -----------------------
  const updateOrdenTrabajoMutation =
    usePutOrdenServicioCorporativo({
      navigate,
      returnUrl: returnUrlInstalacionesTecnicoPage,
      customMessage: 'Orden de trabajo subida correctamente',
      customSuccessCallback: () => {
        clearAllMaterials();
      },
    });

  ///* handlers ============================================
  //const onSave = async (data: SaveFormDataInstallTecAsig) => {
  const onSave = async (data: any) => {
    if (!isValid) return;
    ////* VALIDATE equipos and materials -----------------------
    const equiposUtilizados: EquiposUtilizadosOTTableType[] =
      useMaterialesUtilizadosStore.getState().equipos_utilizados;
    const materialesUtilizados: EquiposUtilizadosOTTableType[] =
      useMaterialesUtilizadosStore.getState().materiales_utilizados;
    // console.log({equiposUtilizados})

    // validate activacion state ----------------------
    if (
      ordentrabajo?.estado_activacion !==
      EstadosActivacionInstallOTEnumChoice.GESTIONADA
    ) {
      return setAlert(
        true,
        'La orden de trabajo aún no ha sido gestionada por ACTIVACIONES',
        AlertSeverityEnum.error
      );
    }



    let thereAreItemsWithoutSeries = false;
    let thereAreItemsWithZeroQuantity = false;
    let isSelectedONT = false;
    let isMoreThanOneONT = false;
    const zeroQuantity = '0.00';
    let isSelectedFibra = false;
    let isSelectedUTP = false;

    ///* validate EQUIPOS -------------------
    if (!equiposUtilizados.length) {
      return setAlert(
        true,
        'No se han agregado EQUIPOS a la orden de trabajo',
        AlertSeverityEnum.error
      );
    }

    const mappedEquiposUtilizados = equiposUtilizados.map(item => {
      const equiposUtilizadosCantidad = item?.cantidad as number
      return {
        cantidad: equiposUtilizadosCantidad?.toFixed(2),
        codigo: item?.producto_data?.codigo,
        series: item?.selected_series || [],

        contieneSeries: item?.contiene_serie,
        producto_data: item?.producto_data,
      };
    });

    // validation logic -------
    mappedEquiposUtilizados.forEach(item => {
      if (item?.contieneSeries && !item?.series?.length) {
        thereAreItemsWithoutSeries = true;
      }
      if (item?.cantidad === zeroQuantity) {
        thereAreItemsWithZeroQuantity = true;
      }
      if (item?.producto_data?.es_ont) {
        isSelectedONT = true;
      }
    });

    // validation alerts -------
    if (thereAreItemsWithZeroQuantity) {
      setAlert(true, 'Hay EQUIPOS sin cantidad', AlertSeverityEnum.error);
      return;
    }
    if (thereAreItemsWithoutSeries) {
      setAlert(
        true,
        'Hay EQUIPOS sin series asignadas',
        AlertSeverityEnum.error
      );
      return;
    }
    if (!isSelectedONT) {
      setAlert(
        true,
        'No se ha seleccionado el equipo ONT',
        AlertSeverityEnum.error
      );
      return;
    }
    if (isMoreThanOneONT) {
      setAlert(
        true,
        'Solo puede seleccionar un equipo ONT por instalación',
        AlertSeverityEnum.error
      );
      return;
    }
    // validate the same serie ont in equipos and activacion
    // const serieONTActivacion = data?.serie_ont;
    // const serieONT = mappedEquiposUtilizados.find(
    //   item => item?.producto_data?.es_ont
    // )?.series?.[0];
    // if (serieONTActivacion !== serieONT) {
    //   setAlert(
    //     true,
    //     'La serie ONT seleccionada no coincide con la serie ONT asignada: ' +
    //     `[ ${serieONTActivacion} ]`,
    //     AlertSeverityEnum.error
    //   );
    //   return;
    // }

    ///* validate MATERIALES -------------------
    if (!materialesUtilizados.length) {
      return setAlert(
        true,
        'No se han agregado MATERIALES a la orden de trabajo',
        AlertSeverityEnum.error
      );
    }

    const mappedMaterialesUtilizados = materialesUtilizados.map(item => {
      const materialesUtilizadosCantidad = item?.cantidad as number
      return {
        cantidad: materialesUtilizadosCantidad?.toFixed(2),
        codigo: item?.producto_data?.codigo,
        series: item?.selected_series || [],

        contieneSeries: item?.contiene_serie,
        producto_data: item?.producto_data,
      };
    });

    // validation logic -------
    mappedMaterialesUtilizados.forEach(item => {
      // console.log("items:", zeroQuantity)
      if (item?.contieneSeries && !item?.series?.length) {
        thereAreItemsWithoutSeries = true;
      }
      if (item?.cantidad === zeroQuantity) {
        thereAreItemsWithZeroQuantity = true;
      }
      // if (item?.producto_data?.es_fibra) {
      //   isSelectedFibra = true;
      // }
      // if (item?.producto_data?.es_utp) {
      //   isSelectedUTP = true;
      // }
    });

    // validation alerts -------
    if (thereAreItemsWithoutSeries) {
      setAlert(
        true,
        'Hay MATERIALES sin series asignadas',
        AlertSeverityEnum.error
      );
      return;
    }
    if (thereAreItemsWithZeroQuantity) {
      setAlert(true, 'Hay MATERIALES sin cantidad', AlertSeverityEnum.error);
      return;
    }
    if (!isSelectedFibra) {
      setAlert(
        true,
        'No se ha seleccionado el material FIBRA',
        AlertSeverityEnum.error
      );
      return;
    }
    if (!isSelectedUTP) {
      setAlert(
        true,
        'No se ha seleccionado el material UTP',
        AlertSeverityEnum.error
      );
      return;
    }
    ///* END VALIDATE equipos and materials -----------------------

    ///* clean equipos/materiales data ----------------
    const cleanedEquiposUtilizados: EquiposMaterialesUtilizados[] =
      mappedEquiposUtilizados.map(item => {
        return {
          cantidad: item?.cantidad,
          codigo: item?.producto_data?.codigo!,
          series: item?.series,
        };
      });
    const cleanedMaterialesUtilizados: EquiposMaterialesUtilizados[] =
      mappedMaterialesUtilizados.map(item => {
        return {
          cantidad: item?.cantidad,
          codigo: item?.producto_data?.codigo!,
          series: item?.series,
        };
      });

    ///* upd ----------------
    if (!!ordentrabajo) {
      updateOrdenTrabajoMutation.mutate({
        id: ordentrabajo?.id_orden_servicio_corporativo!,
        data: {
          estado_solicitud_servicio_corporativo: EstadosOrdenTrabajoEnumChoice.REALIZADA,
          codigo_bobina: data.codigo_bobina,
          splitter: data.splitter,
          manga: data.manga,
          color_hilo: data.color_hilo,
          num_hilo: data.num_hilo,
          nap: data.nap,
          puerto: data.puerto,
          distancia: data.distancia,
          test_5g: data.test_5g,
          potencia_dbm: data.potencia_dbm,
          potencia_ont: data.potencia_ont,
          es_conexion_tierra: data.es_conexion_tierra,
          es_regulador: data.es_regulador,
          ubicacion_equipo_ac: data.ubicacion_equipo_ac,
          ubicacion_ont: data.ubicacion_ont,
          celular_2: data.celular_2,
          incidencia: data.incidencia,
          prioridad_incidencia: data.prioridad_incidencia,
          observaciones: {
            ...ordentrabajo?.observaciones,
            observacion_tecnico: data.observacion_tecnico,
          },
          punta_final: "0",
            // materialesUtilizados
            //   .find(item => item?.producto_data?.es_fibra)
            //   ?.puntaFinal?.toFixed(2) || '0.00',
          equipos_utilizados: cleanedEquiposUtilizados, ///* TODO: Validar
          materiales_utilizados: cleanedMaterialesUtilizados,
        },
      });

      clearAllMaterials();
      return;
    }
  };

  // to save upds in store
  const setOTStorageUpdTecnico = () => { };

  const refreshData = async () => {
    await queryClient.invalidateQueries({
      queryKey: [
        OrdenTrabajoQueryKeysEnum.OrdenTrabajo,
        ordentrabajo?.id_orden_servicio_corporativo?.toString() || '',
      ],
    });
  };

  const resetFormWithOTData = (ordenTrabajo: IOrdenServicioCorporativo) => {
    if (!ordenTrabajo?.id_orden_servicio_corporativo) return;

    const { prospecto_corporativo_data, preventa_corporativa_data: preventa_coorporativa_data, agendamiento_corporativo_data, ...rest } =
      ordenTrabajo;

    //delete rest?.contrato_data;

    ///* reset form -------------
    reset({
      ...prospecto_corporativo_data,
      ...preventa_coorporativa_data,
      ...agendamiento_corporativo_data,
      ...rest,
    } as unknown as any);
  };

  ///* effects -----------------------
  useEffect(() => {
    if (!ordentrabajo?.id_orden_servicio_corporativo) return;

    resetFormWithOTData(ordentrabajo);
  }, [ordentrabajo]);

  return (
    <TabsFormBoxScene
      titlePage={title}
      showBtns={false}
      // // custom btns ---------
      showCustomBtns={true}
      customBtns={
        <CustomButtunsRejectConfirm
          confirmTextBtn={isCorrentingData ? 'Actualizar' : 'Guardar'}
          showRejectBtn={!isCorrentingData}
          rejectTextBtn={"Rechazar actividad"}
          onCancel={() => {
            setConfirmDialog({
              isOpen: true,
              title: 'Abandonar Formulario',
              subtitle:
                '¿Está seguro de abandonar el formulario? Se perderán todos los cambios realizados.',
              onConfirm: () => {
                setConfirmDialogIsOpen(false);
                clearAllMaterials();
                navigate(returnUrlInstalacionesTecnicoPage);

                // setOTinStore(null);
                // clearAllMaterials();
                // clearAllOTStore();
              },
              confirmTextBtn: 'SI',
              cancelTextBtn: 'NO',
            });
          }}
          //  ----------- CONFIRMAR -----------
          onConfirm={handleSubmit(onSave, err => {
            console.log(err);
            
            setOTStorageUpdTecnico();
            if (
              err.ipv4_actual?.message ||
              err.puerto?.message ||
              err.pppoe?.message ||
              err.pppassword?.message ||
              ordentrabajo?.estado_activacion !==
              EstadosActivacionInstallOTEnumChoice.GESTIONADA
            ) {
              return setAlert(
                true,
                'La orden de trabajo aún no ha sido gestionada por ACTIVACIONES',
                AlertSeverityEnum.error
              );
            }
            // console.log(form.getValues())
            setAlert(true, 'Faltan campos por llenar', AlertSeverityEnum.error);
          })}
          //  ----------- RECHAZAR -----------
          onReject={() => {
            // Prerechazo
            setOpenRejectModal(true);
          }}
          // auxiliar btn (refresh) ----------------
          showAuxiliarBtn={!isCorrentingData}
          auxiliarBtn={
            <SingleIconButton
              label="Actualizar"
              color="success"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setConfirmDialog({
                  isOpen: true,
                  title: 'Refrescar Datos',
                  subtitle:
                    'Al refrescar los datos se perderán los cambios realizados. ¿Desea continuar?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    refreshData();
                  },
                  confirmTextBtn: 'SI',
                  cancelTextBtn: 'NO',
                });
              }}
            />
          }
        />
      }
      // // tabs ---------
      tabs={
        <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
          <Tab label="ORDEN TRABAJO" value={1} {...a11yProps(1)} />
          <Tab label="DETALLE INSTALACIÓN" value={2} {...a11yProps(2)} />
          <Tab label="INFORMACIÓN CLIENTE" value={3} {...a11yProps(3)} />
          <Tab label="MATERIALES" value={4} {...a11yProps(4)} />
        </FormTabsOnly>
      }
    >
      {/* ===================== ORDEN TRABAJO ===================== */}
      <CustomTabPanel index={valueTab} value={1}>
        <OTInstalacionTecnicoCorporativoFormTab
          form={form}
          errors={errors}
          shrink={shrink}
        />
      </CustomTabPanel>

      {/* ===================== DETALLES DE INSTALACIÓN ===================== */}
      <CustomTabPanel index={valueTab} value={2}>
        <OTDeralleInstallTecnicoCorporativoFormTab
          form={form}
          errors={errors}
          shrink={shrink}
          coords={ordentrabajo?.preventa_corporativa_data.coordenada_servicio || ''}
          ordenTrabajo={ordentrabajo!}
          canDragMarker
          canRuler
        />
      </CustomTabPanel>

      {/* ===================== DETALLES CLIENTE ===================== */}
      <CustomTabPanel index={valueTab} value={3}>
        <OTDetalleClienteInstallTecnicoCorporativoFormTab
          form={form}
          errors={errors}
          shrink={shrink}
        />
      </CustomTabPanel>

      {/* ===================== MATERIALES ===================== */}
      <CustomTabPanel index={valueTab} value={4}>
        <OTMaterialesInstallTecnicoCorporativoFormTab
          form={form}
          ordenTrabajo={ordentrabajo!}
        />
      </CustomTabPanel>

      {/* =================== DIALOGS =================== */}
      <RejectInstalacionAsigTecModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        idOrdenTrabajo={ordentrabajo?.id_orden_servicio_corporativo!}
        observacionesOrdenTrabajo={ordentrabajo?.observaciones}
        //ordenTrabajo={ordentrabajo!}
      />
    </TabsFormBoxScene>
  );
};

export default SaveOrdenTrabajoAsignadaTecCorporativo;
